import styled, { css } from 'styled-components';
import { mixin } from '@wconcept/wconcept-ui-renewal/styles/index';

type TNavType = 'black' | 'white' | 'bottom01' | 'outside01';
type TNavSize = 'xsmall' | 'small' | 'medium';

interface IProps {
  className?: string;
  type?: TNavType | undefined;
  size?: TNavSize | undefined;
  $bgType?: 'white' | 'black' | undefined;
  $offsetTop?: number;
}

/** 이전 / 다음 / 자동재생 버튼 3형제 */
const BTN =
  '.swiper-button-autoplay-control, .swiper-button-next, .swiper-button-prev';

/* ---------------------------------------------------------------------------
 * 공통 조각
 *
 * 공통에는 "모든 타입이 항상 같은 값"만 둔다.
 * 배경·크기·색 같은 스킨을 공통에 두면 새 타입을 추가할 때마다 그 값을 되돌리는
 * 코드(background: transparent !important, border-radius: 0 ...)를 함께 써야 한다.
 * 스킨은 아래 TYPE 블록 안에서만 정의한다.
 * ------------------------------------------------------------------------- */

/** 위치·정렬·상태 (스킨 없음) */
const layout = css`
  ${BTN} {
    ${mixin.flex({ display: 'inline-flex' })};
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1;
    cursor: pointer;
    transition: all 0.2s;

    svg {
      transform: translateX(1px);
    }
  }

  /* 다음 화살표 아이콘을 180도 돌려 이전 화살표로 쓴다 */
  .swiper-button-prev svg {
    transform: rotate(180deg) translateX(0px);
  }
`;

/** 첫/마지막 슬라이드에서 해당 화살표를 감춘다 */
const hideWhenDisabled = css`
  ${BTN} {
    &.swiper-button-disabled {
      cursor: default;
      pointer-events: auto;
      display: none;
    }

    /* 상품 수가 slidesPerView 이하라 스와이프가 아예 불가능하면(Locked) 둘 다 감춘다 */
    &.swiper-button-lock {
      display: none;
    }
  }
`;

/** 리스트 좌우 "안쪽" 8px 에 붙인다 */
const insideEdge = css`
  .swiper-button-next {
    right: 8px;
  }

  .swiper-button-prev {
    left: 8px;
  }
`;

/** black / white 가 함께 쓰는 둥근 버튼 : 사이즈별 박스 + 아이콘 + 자동재생 컨트롤 */
const ROUND = {
  xsmall: {
    box: '28px',
    radius: '4px',
    iconW: '7px',
    iconH: '12px',
    autoplay: css`
      .play svg {
        width: 14px;
        height: 14px;
      }

      .pause {
        width: 14px;
        column-gap: 4px;

        .line {
          width: 2px;
          height: 10px;
        }
      }
    `,
  },
  small: {
    box: '36px',
    radius: '5px',
    iconW: '8px',
    iconH: '13px',
    autoplay: null,
  },
  medium: {
    box: '44px',
    radius: '7px',
    iconW: '9px',
    iconH: '14px',
    autoplay: null,
  },
};

const roundSkin = (size: TNavSize = 'small') => {
  const spec = ROUND[size] ?? ROUND.small;

  return css`
    ${BTN} {
      opacity: 1;
      visibility: visible;
      width: ${spec.box};
      height: ${spec.box};
      border-radius: ${spec.radius};

      svg {
        width: ${spec.iconW};
        height: ${spec.iconH};

        path {
          fill: #fff;
        }
      }

      &.swiper-button-autoplay-control {
        .play {
          ${mixin.flex({})};

          svg {
            width: auto;
            height: auto;
            transform: none;
          }
        }

        .pause {
          ${mixin.flex({})};

          .line {
            display: inline-block;
            background-color: #fff;
          }
        }

        ${spec.autoplay};
      }
    }
  `;
};

/* ---------------------------------------------------------------------------
 * 타입별 스킨
 *
 * 각 타입은 자기 모양을 처음부터 끝까지 스스로 정의한다.
 * 다른 타입 값을 지우는 코드가 들어가면 안 된다 — 그럴 필요가 생겼다면
 * 그 값이 공통에 잘못 올라가 있다는 뜻이다.
 * ------------------------------------------------------------------------- */
const TYPE: Record<TNavType, (size?: TNavSize) => ReturnType<typeof css>> = {
  /** black : 반투명 검정 라운드 버튼 (기본값) */
  black: size => css`
    ${roundSkin(size)};
    ${insideEdge};
    ${hideWhenDisabled};

    ${BTN} {
      background: rgba(0, 0, 0, 0.1);

      &:not(.swiper-button-disabled):hover {
        background: rgba(0, 0, 0, 0.7);
      }
    }
  `,

  /** white : 어두운 배경 위에 올리는 흰 테두리 버튼 */
  white: size => css`
    ${roundSkin(size)};
    ${insideEdge};
    ${hideWhenDisabled};

    ${BTN} {
      border-radius: 5px;
      border: 1px solid rgba(255, 255, 255, 0.2);
      background: transparent;

      &:not(.swiper-button-disabled):hover {
        border: 1px solid #fff;
      }
    }
  `,

  /** bottom01 : 배경 없이 아이콘만. 하단 중앙 인디케이터 바에 얹어 쓴다. */
  bottom01: size => {
    const isMedium = size === 'medium';

    return css`
      ${insideEdge};

      ${BTN} {
        opacity: 1;
        visibility: visible;
        width: ${isMedium ? '18px' : '14px'};
        height: ${isMedium ? '18px' : '14px'};
        background: transparent;

        svg {
          width: ${isMedium ? '10px' : '8px'};
          height: ${isMedium ? '16px' : '13px'};

          path {
            fill: #000;
          }
        }

        &.swiper-button-autoplay-control {
          .play {
            ${mixin.flex({})};

            svg {
              width: 13px;
              height: 14px;
              transform: none;
            }
          }

          .pause {
            ${mixin.flex({})};
            width: 10.5px;
            column-gap: 5.5px;

            .line {
              display: inline-block;
              width: 2.5px;
              height: 15px;
              background-color: #000;
            }
          }
        }

        /* 끝에 닿아도 감추지 않고 회색으로 남긴다 */
        &.swiper-button-disabled {
          cursor: default;
          pointer-events: auto;

          svg path {
            fill: #ccc;
          }
        }
      }
    `;
  },

  /**
   * outside01 : 리스트 좌우 "바깥"에 붙는 화살표.
   * 원형 카테고리 행처럼 썸네일 열 밖에 화살표가 나가는 케이스에 쓴다.
   * 세로는 offsetTop 이 아니라 top:0 기준 — 썸네일 열 높이(114px)를 그대로 덮는
   * 히트영역이라 리스트 상단에 맞춘다.
   */
  outside01: () => css`
    ${hideWhenDisabled};

    ${BTN} {
      opacity: 1;
      visibility: visible;
      width: 57px;
      height: 114px;
      margin: 0;
      top: 0;
      transform: none;
      background: transparent;
    }

    .swiper-button-prev {
      left: -57px;
    }

    .swiper-button-next {
      right: -57px;
    }
  `,
};

export const SwiperNavigation = styled.div<IProps>`
  ${layout};

  ${props =>
    props.$offsetTop
      ? css`
          .swiper-button-next,
          .swiper-button-prev {
            transform: unset;
            top: ${props.$offsetTop}px;
          }
        `
      : null}

  ${props => (TYPE[props.type ?? 'black'] ?? TYPE.black)(props.size)}

  ${props =>
    props.$bgType === 'black'
      ? css`
          /* 어두운 배경 위 bottom01 : 아이콘을 흰색으로 뒤집는다 */
          &.swiper-navigation-bottom01 {
            ${BTN} {
              svg path {
                fill: #fff;
              }

              &.swiper-button-disabled svg path {
                fill: #777;
              }
            }
          }
        `
      : null}
`;
